import { dirForTranscriptSnippet } from './rtlText.js';

/**
 * Transcript - Manages transcript display and conversation history
 *
 * Required from parent (voiceInterface):
 * - config: { language, translations }
 * - shadowRoot: ShadowRoot | Document
 * - isMobile: boolean
 * - conversationExpanded: boolean
 * - messages: Array (for mobile)
 * - t(key): string (translation helper method)
 * - escapeHtml(text): string
 * - addMobileMessage(type, text): void (when mobile)
 *
 * State owned by this module:
 * - conversationMessages: Array<{role, content}>
 */

export class Transcript {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    this.conversationMessages = [];
    // Track current live message (being streamed/typed)
    this.currentLiveMessage = null; // {role: 'assistant'|'user', content: string} or null
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Helper to access escapeHtml method from parent
   */
  escapeHtml(text) {
    return this.voiceInterface.escapeHtml(text);
  }

  /**
   * Update transcript text (desktop and mobile)
   */
  updateTranscript(text) {
    console.log('📝 [TRANSCRIPT] updateTranscript() called with text:', text ? text.substring(0, 50) + '...' : 'null/empty');

    const cfg = this.voiceInterface.config;
    const hasContent = !!(text && text.trim());
    const snippetForDir = hasContent ? text : (this.t('transcriptWillAppear') || '');
    const assistantDir = dirForTranscriptSnippet(snippetForDir, cfg);

    // Mobile transcript (use container-scoped lookup for Shopify/embedded contexts)
    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    if (mobileBar) {
      const mobileTranscriptText = mobileBar.querySelector('#mobileTranscriptText');
      if (mobileTranscriptText) {
        mobileTranscriptText.setAttribute('dir', assistantDir);
        mobileTranscriptText.textContent = text;
        mobileTranscriptText.style.color = '';
      }

      // Update last message or add new one
      if (this.voiceInterface.messages.length > 0 && this.voiceInterface.messages[this.voiceInterface.messages.length - 1].type === 'assistant') {
        this.voiceInterface.messages[this.voiceInterface.messages.length - 1].text = text;
      } else {
        this.voiceInterface.messages.push({ type: 'assistant', text });
      }

      // Update expanded messages container (scoped to our bar)
      const messagesContainer = mobileBar.querySelector('#mobileMessagesContainer');
      if (messagesContainer) {
        const lastMessage = messagesContainer.querySelector('.mobile-message-bubble.assistant:last-child');
        if (lastMessage) {
          lastMessage.textContent = text;
          lastMessage.setAttribute('dir', dirForTranscriptSnippet(text, cfg));
        } else {
          this.voiceInterface.addMobileMessage('assistant', text);
        }
      }
      return; // Skip desktop transcript on mobile
    }

    // Desktop transcript - Update collapsed live text (2 lines)
    const liveTextCollapsed = this.voiceInterface.shadowRoot.getElementById('liveTextCollapsed');
    if (liveTextCollapsed) {
      liveTextCollapsed.setAttribute('dir', assistantDir);
      liveTextCollapsed.style.color = '';
      if (text && text.trim()) {
        liveTextCollapsed.textContent = text;
        liveTextCollapsed.classList.remove('muted');
        // Re-add cursor
        const cursor = document.createElement('span');
        cursor.className = 'typing-cursor';
        liveTextCollapsed.appendChild(cursor);
      } else {
        liveTextCollapsed.textContent = this.t('transcriptWillAppear') || 'Transcript will appear here...';
        liveTextCollapsed.classList.add('muted');
      }
    }

    // Update live message in expanded history
    if (text && text.trim()) {
      // Check if this is a continuation of the current live message or a new one
      const isStreamingUpdate = this.currentLiveMessage && 
                                this.currentLiveMessage.role === 'assistant' &&
                                text.startsWith(this.currentLiveMessage.content);
      
      if (isStreamingUpdate) {
        // Streaming update - just update the live message
        this.currentLiveMessage.content = text;
      } else {
        // New message starting - finalize previous live message if exists
        if (this.currentLiveMessage) {
          this.finalizeLiveMessage();
        }
        // Start new live message
        this.currentLiveMessage = { role: 'assistant', content: text };
      }
      
      // Update live message row UI
      this.updateLiveMessageRow();
    } else {
      // Empty text - hide live message
      this.currentLiveMessage = null;
      this.updateLiveMessageRow();
    }

    // Auto-scroll if expanded
    if (this.voiceInterface.conversationExpanded) {
      const history = this.voiceInterface.shadowRoot.getElementById('conversationHistory');
      if (history) {
        setTimeout(() => {
          history.scrollTop = history.scrollHeight;
        }, 10);
      }
    }
  }

  /**
   * Toggle conversation panel expand/collapse
   */
  toggleConversationPanel() {
    if (this.voiceInterface.isMobile) return;

    this.voiceInterface.conversationExpanded = !this.voiceInterface.conversationExpanded;
    
    const panel = this.voiceInterface.shadowRoot.getElementById('desktopConversationPanel');
    const header = this.voiceInterface.shadowRoot.getElementById('conversationHeader');
    const toggleText = this.voiceInterface.shadowRoot.getElementById('conversationToggleText');
    const originalSection = this.voiceInterface.shadowRoot.getElementById('desktopVoiceSection');
    const compactSection = this.voiceInterface.shadowRoot.getElementById('compactVoiceSection');

    if (panel) {
      if (this.voiceInterface.conversationExpanded) {
        panel.classList.remove('collapsed');
        panel.classList.add('expanded');
        if (header) header.classList.add('expanded');
        if (toggleText) toggleText.textContent = 'Hide history';
        
        if (originalSection) originalSection.style.display = 'none';
        if (compactSection) compactSection.style.display = 'flex';
        
        console.log('📝 [TRANSCRIPT] Expanding panel, rendering', this.conversationMessages?.length || 0, 'messages');
        this.renderConversationMessages();
      } else {
        panel.classList.remove('expanded');
        panel.classList.add('collapsed');
        if (header) header.classList.remove('expanded');
        if (toggleText) toggleText.textContent = 'Show history';
        
        if (originalSection) originalSection.style.display = '';
        if (compactSection) compactSection.style.display = 'none';
      }
    }
  }

  /**
   * Finalize the current live message (move it to history)
   */
  finalizeLiveMessage() {
    if (!this.currentLiveMessage || !this.currentLiveMessage.content || !this.currentLiveMessage.content.trim()) {
      return;
    }
    
    // Add to conversation history
    if (!this.conversationMessages) {
      this.conversationMessages = [];
    }
    
    this.conversationMessages.push({
      role: this.currentLiveMessage.role,
      content: this.currentLiveMessage.content
    });
    
    console.log('📝 [TRANSCRIPT] Finalized live message:', this.currentLiveMessage.role, this.currentLiveMessage.content.substring(0, 50) + '...');
    
    // Clear live message
    this.currentLiveMessage = null;
    
    // Render if panel is expanded
    if (this.voiceInterface.conversationExpanded) {
      this.renderConversationMessages();
    }
  }

  /**
   * Add message to conversation history
   * When a new message starts (user sends or assistant starts new response), finalize previous live message
   */
  addConversationMessage(role, content) {
    if (this.voiceInterface.isMobile) return;

    if (!content || !content.trim()) {
      return; // Skip empty messages
    }

    // Finalize previous live message if exists (new message is starting)
    if (this.currentLiveMessage) {
      this.finalizeLiveMessage();
    }

    if (!this.conversationMessages) {
      this.conversationMessages = [];
    }

    // User messages are added directly to history (they're not streamed)
    // Assistant messages should go through updateTranscript() for streaming handling
    // If we get here with assistant role, it means a new message is starting
    if (role === 'user') {
      this.conversationMessages.push({ role, content });
      console.log('📝 [TRANSCRIPT] User message added to history');
    } else {
      // Assistant message - this shouldn't normally happen via addConversationMessage
      // But if it does, set as live (will be finalized when next message starts)
      this.currentLiveMessage = { role, content };
      this.updateLiveMessageRow();
      console.log('📝 [TRANSCRIPT] Assistant message set as live (via addConversationMessage)');
      return; // Don't add to history yet, it's live
    }

    // Render messages if panel is expanded
    if (this.voiceInterface.conversationExpanded) {
      this.renderConversationMessages();
    }
  }

  /**
   * Update the live message row UI
   */
  updateLiveMessageRow() {
    const liveMessageRow = this.voiceInterface.shadowRoot.getElementById('liveMessageRow');
    const liveMessageText = this.voiceInterface.shadowRoot.getElementById('liveMessageText');
    const liveMessageAvatar = liveMessageRow?.querySelector('.message-avatar');
    
    if (!liveMessageRow || !liveMessageText) return;
    
    if (this.currentLiveMessage && this.currentLiveMessage.content && this.currentLiveMessage.content.trim()) {
      // Update avatar based on role
      if (liveMessageAvatar) {
        if (this.currentLiveMessage.role === 'user') {
          liveMessageAvatar.textContent = 'U';
          liveMessageAvatar.className = 'message-avatar user';
        } else {
          liveMessageAvatar.innerHTML = '<div class="orb"></div>';
          liveMessageAvatar.className = 'message-avatar assistant';
        }
      }
      
      // Update message text
      liveMessageText.textContent = this.currentLiveMessage.content;
      liveMessageRow.style.display = 'flex';

      const bubble = liveMessageRow.querySelector('.message-bubble');
      if (bubble) {
        bubble.setAttribute('dir', dirForTranscriptSnippet(this.currentLiveMessage.content, this.voiceInterface.config));
      }
      
      // Re-add cursor
      const cursor = liveMessageText.querySelector('.typing-cursor') || document.createElement('span');
      cursor.className = 'typing-cursor';
      if (!liveMessageText.querySelector('.typing-cursor')) {
        liveMessageText.appendChild(cursor);
      }
    } else {
      liveMessageRow.style.display = 'none';
    }
  }

  /**
   * Clear conversation messages
   */
  clearConversationMessages() {
    this.conversationMessages = [];
    this.currentLiveMessage = null;
    // Hide live message row
    const liveMessageRow = this.voiceInterface.shadowRoot.getElementById('liveMessageRow');
    if (liveMessageRow) {
      liveMessageRow.style.display = 'none';
    }
  }

  /**
   * Render conversation messages
   * Excludes the current live message (shown separately in liveMessageRow)
   */
  renderConversationMessages() {
    const container = this.voiceInterface.shadowRoot.getElementById('conversationMessages');
    if (!container) {
      console.warn('📝 [TRANSCRIPT] conversationMessages container not found');
      return;
    }

    if (!this.conversationMessages || this.conversationMessages.length === 0) {
      console.log('📝 [TRANSCRIPT] No messages to render');
      container.innerHTML = '';
      return;
    }

    // Filter out the last message if it matches the current live message
    let messagesToRender = [...this.conversationMessages];
    if (this.currentLiveMessage && messagesToRender.length > 0) {
      const lastMessage = messagesToRender[messagesToRender.length - 1];
      if (lastMessage.role === this.currentLiveMessage.role && 
          lastMessage.content === this.currentLiveMessage.content) {
        // Last message is the live one - exclude it from history
        messagesToRender = messagesToRender.slice(0, -1);
      }
    }

    const bubbleDir = dirForTranscriptSnippet(msg.content, this.config);
    console.log('📝 [TRANSCRIPT] Rendering', messagesToRender.length, 'messages (live message excluded)');
    container.innerHTML = messagesToRender.map(msg => {
      const isUser = msg.role === 'user';
      return `
        <div class="message-row ${msg.role}">
          <div class="message-avatar ${msg.role}">
            ${isUser ? 'U' : '<div class="orb"></div>'}
          </div>
          <div class="message-bubble" dir="${bubbleDir}">${this.escapeHtml(msg.content)}</div>
        </div>
      `;
    }).join('');

    // Scroll to bottom
    const history = this.voiceInterface.shadowRoot.getElementById('conversationHistory');
    if (history) {
      setTimeout(() => {
        history.scrollTop = history.scrollHeight;
      }, 10);
    }
  }
}
